/**
 * Muscle Group Volume Service
 *
 * Training Distribution (Stats > Progress) needs "based on completed
 * training volume" — the pre-existing inline getMuscleGroupFrequency in
 * EnhancedStats.jsx counts logged sets, not volume, so it can't be reused
 * here without mislabeling the metric. This mirrors WeeklyVolumeChart's
 * fetch/aggregation pattern (same completed-only filter, same
 * getSetVolume/getEffectiveWeight utility) but buckets by movement_category
 * instead of by week.
 *
 * Primary + secondary-muscle crediting matches the coach's "Muscle Group
 * Split" (ClientManagement.jsx loadPerformanceAnalytics) exactly, so the two
 * views reconcile when viewed over the same time range: a category gets full
 * credit for an exercise's volume as its primary movement_category, plus 30%
 * credit toward each of its secondary muscles' primary categories.
 */

import { supabase } from '../supabase';
import { getSetVolume } from '../utils/weightUtils';
import { SECONDARY_MUSCLES } from '../constants/exerciseCategories';

const EXCLUDED_CATEGORIES = ['Cardio', 'Stretch'];

/**
 * Get lifted-volume distribution across movement categories (primary +
 * 30%-credited secondary muscles), excluding Cardio/Stretch.
 *
 * @param {string} userId - User's auth ID
 * @returns {Array} - [{ category, volume, percent }], sorted desc by volume.
 *   Only categories with volume > 0 are included.
 */
/**
 * Whole percentages that add up to exactly 100 (largest remainder): plain rounding can give 99 or 101
 * (Codex's review of the client's Stats, task 017, M5).
 */
export const wholePercents = (values) => {
  const total = values.reduce((sum, v) => sum + v, 0);
  if (total <= 0) return values.map(() => 0);
  const exact = values.map(v => (v / total) * 100);
  const result = exact.map(Math.floor);
  let left = 100 - result.reduce((sum, v) => sum + v, 0);
  exact
    .map((v, i) => ({ i, remainder: v - Math.floor(v) }))
    .sort((a, b) => b.remainder - a.remainder)
    .forEach(({ i }) => { if (left > 0) { result[i] += 1; left -= 1; } });
  return result;
};

export const getMuscleGroupVolume = async (userId, { strict = false } = {}) => {
  try {
    const { data: logs, error: logsError } = await supabase
      .from('workout_logs')
      .select('weight, reps, exercise_id')
      .eq('user_id', userId)
      .eq('completed', true)
      .not('weight', 'is', null)
      .limit(10000);

    if (logsError) throw logsError;
    if (!logs || logs.length === 0) return [];

    const { data: userData } = await supabase
      .from('users')
      .select('bodyweight_kg')
      .eq('auth_user_id', userId)
      .single();
    const userBodyweight = userData?.bodyweight_kg || 0;

    // movement_category + secondary_muscles live on `exercises`, not
    // `workout_logs` (which only carries a denormalized copy that isn't
    // reliably populated) — same join EnhancedStats.jsx's original
    // getMuscleGroupFrequency used.
    const exerciseIds = [...new Set(logs.map(l => l.exercise_id).filter(Boolean))];
    const { data: exercises } = exerciseIds.length > 0
      ? await supabase
          .from('exercises')
          .select('id, movement_category, is_bodyweight, is_assisted, bodyweight_multiplier, secondary_muscles')
          .in('id', exerciseIds)
      : { data: [] };
    const exerciseMap = new Map(exercises?.map(e => [e.id, e]) || []);

    // Build inverted lookup: specific muscle name → primary category
    const secondaryToCategory = {};
    Object.entries(SECONDARY_MUSCLES).forEach(([cat, muscles]) => {
      muscles.forEach(m => { secondaryToCategory[m] = cat; });
    });

    // Sum volume per exercise first — secondary-muscle credit is 30% of an
    // exercise's TOTAL volume, not per set.
    const exerciseVolume = new Map();
    logs.forEach(log => {
      const exercise = exerciseMap.get(log.exercise_id);
      if (!exercise || !exercise.movement_category) return;
      if (EXCLUDED_CATEGORIES.includes(exercise.movement_category)) return;
      const volume = getSetVolume(log.weight, log.reps, exercise, userBodyweight);
      if (!volume) return;
      exerciseVolume.set(log.exercise_id, (exerciseVolume.get(log.exercise_id) || 0) + volume);
    });

    const categoryVolume = new Map();
    exerciseVolume.forEach((vol, exerciseId) => {
      const exercise = exerciseMap.get(exerciseId);
      const category = exercise.movement_category;

      // Primary category — full credit
      categoryVolume.set(category, (categoryVolume.get(category) || 0) + vol);

      // Secondary muscles — 30% credit, mapped to their primary category
      const secondaryMuscles = exercise.secondary_muscles
        ? exercise.secondary_muscles.split(',').map(m => m.trim()).filter(Boolean)
        : [];
      secondaryMuscles.forEach(muscle => {
        const cat = secondaryToCategory[muscle];
        if (!cat || cat === category || EXCLUDED_CATEGORIES.includes(cat)) return;
        categoryVolume.set(cat, (categoryVolume.get(cat) || 0) + vol * 0.3);
      });
    });

    const total = Array.from(categoryVolume.values()).reduce((sum, v) => sum + v, 0);
    if (total === 0) return [];

    const entries = Array.from(categoryVolume.entries());
    const percents = wholePercents(entries.map(([, volume]) => volume));
    return entries
      .map(([category, volume], i) => ({
        category,
        volume: Math.round(volume),
        percent: percents[i],
      }))
      .sort((a, b) => b.volume - a.volume);
  } catch (error) {
    console.error('Error getting muscle group volume:', error);
    if (strict) throw error;
    return [];
  }
};
